import { useState, useRef } from 'react';
import { ChevronDown, Filter, Power, AlertTriangle, AlertCircle, RotateCcw, RefreshCw, Search, X } from 'lucide-react';
import { useIsMobile } from '@/hooks/useMediaQuery';
import { useUserRole } from '@/hooks/useUserRole';
import { useRBAC } from '@/contexts/RBACContext';
import { useFilterOnboarding } from '@/hooks/useFilterOnboarding';
import type { AlertFilterState } from '@/lib/unitFilters';
import type { Unit, UnitFilterData } from '@/types';
import { MODEL_LABELS } from '@/lib/constants';
import { CompanyGroupFilters } from './CompanyGroupFilters';
import { UnitModelFilter } from './UnitModelFilter';
import { IndustryFilter } from './IndustryFilter';
import { OnboardingTooltip } from '@/components/ui/onboarding-tooltip';
import { useFilters } from '@/contexts/FilterContext';
import { Input } from '@/components/ui/input';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuCheckboxItem,
  DropdownMenuTrigger,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';

interface StatusBarProps {
  onlineCount: number;
  offlineCount: number;
  idleCount: number;
  warningCount: number;
  faultCount: number;
  onClearFilters: () => void;
  onRefresh?: () => void;
  isRefreshing?: boolean;
  statusFilters: Set<'online' | 'offline' | 'idle' | 'unknown'>;
  onStatusFiltersChange: (filters: Set<'online' | 'offline' | 'idle' | 'unknown'>) => void;
  showAlerts?: AlertFilterState;
  onShowAlertsChange?: (show: AlertFilterState) => void;
  units: (Unit | UnitFilterData)[];
  searchQuery: string;
  onSearchChange: (query: string) => void;
  searchOpen?: boolean;
  onSearchOpenChange?: (open: boolean) => void;
  /** When true, content (map/dashboard) has finished loading - enables onboarding tooltips */
  contentLoaded?: boolean;
}

export function StatusBar({
  onlineCount,
  offlineCount,
  idleCount,
  warningCount,
  faultCount,
  onClearFilters,
  onRefresh,
  isRefreshing = false,
  statusFilters,
  onStatusFiltersChange,
  showAlerts = 'none',
  onShowAlertsChange,
  units,
  searchQuery,
  onSearchChange,
  searchOpen = false,
  onSearchOpenChange,
  contentLoaded = false,
}: StatusBarProps) {
  const [open, setOpen] = useState(false);
  const [isSpinning, setIsSpinning] = useState(false);
  const isMobile = useIsMobile();
  const { isGroupAdmin } = useUserRole();
  // Faults and warnings are staff only (Eric, 2026-11-02: "we never wanted anyone else to see the
  // errors"). Customers get no counts from the server, so they get no Error filter either.
  const { isInternal: showErrorFilter } = useRBAC();
  const { selectedCompanyIds, selectedGroupIds, selectedModels, selectedIndustryTypes, companies, groups, isLoading: filterContextLoading } = useFilters();

  // Refs for filter buttons (for tooltip positioning)
  const industryButtonRef = useRef<HTMLButtonElement>(null);
  const companyButtonRef = useRef<HTMLButtonElement>(null);
  const groupButtonRef = useRef<HTMLButtonElement>(null);
  const modelButtonRef = useRef<HTMLButtonElement>(null);

  // Filter onboarding tooltips (priority: industry > company > group > model)
  const {
    showIndustryOnboarding,
    showCompanyOnboarding,
    showGroupOnboarding,
    showModelOnboarding,
    industryMessage,
    companyMessage,
    groupMessage,
    modelMessage,
    dismissIndustry,
    dismissCompany,
    dismissGroup,
    dismissModel,
  } = useFilterOnboarding({ contentLoaded });

  const isSearchOpen = searchOpen;
  const setIsSearchOpen = (open: boolean) => {
    onSearchOpenChange?.(open);
  };

  // Check if filters are at default state (all selected for companies/groups/models/industries, all statuses, no alerts)
  const isFiltersDefault =
    selectedCompanyIds.length === companies.length &&
    selectedGroupIds.length === groups.length &&
    selectedModels.length === Object.keys(MODEL_LABELS).length &&
    selectedIndustryTypes.length > 0 &&
    statusFilters.size === 4 &&
    statusFilters.has('online') &&
    statusFilters.has('offline') &&
    statusFilters.has('idle') &&
    statusFilters.has('unknown') &&
    showAlerts === 'none';

  const handleFilterToggle = (status: 'online' | 'offline' | 'idle') => {
    const newFilters = new Set(statusFilters);
    if (newFilters.has(status)) {
      newFilters.delete(status);
    } else {
      newFilters.add(status);
    }
    // Ensure at least one filter is always selected
    if (newFilters.size === 0) {
      newFilters.add('online');
      newFilters.add('offline');
      newFilters.add('idle');
    }
    onStatusFiltersChange(newFilters);
  };

  // Handle warning filter toggle
  const handleWarningToggle = () => {
    if (!onShowAlertsChange) return;

    if (showAlerts === 'warnings') {
      onShowAlertsChange('none');
    } else if (showAlerts === 'faults') {
      onShowAlertsChange('both');
    } else if (showAlerts === 'both') {
      onShowAlertsChange('faults');
    } else {
      onShowAlertsChange('warnings');
    }
  };

  // Handle fault filter toggle
  const handleFaultToggle = () => {
    if (!onShowAlertsChange) return;

    if (showAlerts === 'faults') {
      onShowAlertsChange('none');
    } else if (showAlerts === 'warnings') {
      onShowAlertsChange('both');
    } else if (showAlerts === 'both') {
      onShowAlertsChange('warnings');
    } else {
      onShowAlertsChange('faults');
    }
  };

  // Handle refresh button click with animation
  const handleRefresh = () => {
    if (isRefreshing || !onRefresh) return;
    
    // Trigger spin animation
    setIsSpinning(true);
    
    // Call the refresh function
    onRefresh();
    
    // Stop spinning after 1 second (or when isRefreshing becomes false)
    setTimeout(() => {
      setIsSpinning(false);
    }, 1000);
  };

  // Mobile layout
  if (isMobile) {
    // Show search input when search is open
    if (isSearchOpen) {
      return (
        <div className="h-12 bg-card border-b border-border flex items-center px-3 gap-2 relative z-20">
          <button
            onClick={() => setIsSearchOpen(false)}
            className="flex items-center justify-center rounded hover:bg-accent transition-colors h-9 w-9 shrink-0"
          >
            <ChevronDown className="h-4 w-4" />
          </button>
          <div className="relative flex-1">
            <Input
              type="search"
              placeholder={isGroupAdmin ? "SEARCH LOCATION, UNIT, PRODUCTION, OR SITE" : "SEARCH LOCATION, UNIT, COMPANY, OR GROUP"}
              className="h-9 text-xs bg-transparent border-0 rounded-none focus-visible:ring-0 focus-visible:ring-offset-0 focus-visible:outline-none focus:ring-0 focus:outline-none focus:border-0 active:border-0 w-full pr-8 [&::-webkit-search-cancel-button]:hidden [&::-webkit-search-decoration]:hidden"
              value={searchQuery}
              onChange={(e) => onSearchChange(e.target.value)}
              autoFocus
            />
            {searchQuery && (
              <button
                onClick={() => onSearchChange('')}
                className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center justify-center hover:bg-accent rounded transition-colors h-6 w-6"
              >
                <X className="h-5 w-5 text-foreground" />
              </button>
            )}
          </div>
        </div>
      );
    }

    // Show normal filter controls when search is closed
    return (
      <>
      <div className="h-12 bg-card border-b border-border flex items-center justify-between px-3 gap-2 relative z-20 overflow-visible">
        {/* Mobile Left: Search icon + STATUS Dropdown (Filter icon) */}
        <div className="flex items-center gap-2">
          {/* Search Button */}
          <button
            onClick={() => setIsSearchOpen(true)}
            className="flex items-center justify-center rounded hover:bg-accent transition-colors h-9 w-9 shrink-0"
          >
            <Search className="h-4 w-4" />
          </button>

          {/* STATUS Dropdown (Filter icon) */}
          <DropdownMenu open={open} onOpenChange={setOpen}>
          <DropdownMenuTrigger asChild>
            <button className="flex items-center justify-center rounded hover:bg-accent data-[state=open]:bg-accent transition-colors h-9 w-9 shrink-0">
              <Filter className="h-4 w-4" />
            </button>
          </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="w-56">
              <DropdownMenuLabel className="text-xs text-muted-foreground uppercase">Status</DropdownMenuLabel>
              <DropdownMenuCheckboxItem
                checked={statusFilters.has('online')}
                onCheckedChange={() => handleFilterToggle('online')}
              >
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 bg-success rounded-sm flex items-center justify-center">
                    <Power className="w-2.5 h-2.5 text-white" />
                  </div>
                  <span>Online {!filterContextLoading && `(${onlineCount})`}</span>
                </div>
              </DropdownMenuCheckboxItem>
              <DropdownMenuCheckboxItem
                checked={statusFilters.has('offline')}
                onCheckedChange={() => handleFilterToggle('offline')}
              >
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 bg-error rounded-sm flex items-center justify-center">
                    <Power className="w-2.5 h-2.5 text-white" />
                  </div>
                  <span>Offline {!filterContextLoading && `(${offlineCount})`}</span>
                </div>
              </DropdownMenuCheckboxItem>
              <DropdownMenuCheckboxItem
                checked={statusFilters.has('idle')}
                onCheckedChange={() => handleFilterToggle('idle')}
              >
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 bg-gray-600 rounded-sm flex items-center justify-center">
                    <Power className="w-2.5 h-2.5 text-white" />
                  </div>
                  <span>Idle {!filterContextLoading && `(${idleCount})`}</span>
                </div>
              </DropdownMenuCheckboxItem>

              {showErrorFilter && (
                <>
                  <DropdownMenuSeparator />

                  <DropdownMenuLabel className="text-xs text-muted-foreground uppercase">Error</DropdownMenuLabel>
                  <DropdownMenuCheckboxItem
                    checked={showAlerts === 'warnings' || showAlerts === 'both'}
                    onCheckedChange={handleWarningToggle}
                  >
                    <div className="flex items-center gap-2">
                      <div className="w-4 h-4 bg-warning rounded-sm flex items-center justify-center">
                        <AlertTriangle className="w-2.5 h-2.5 text-white" />
                      </div>
                      <span>Warnings {!filterContextLoading && `(${warningCount})`}</span>
                    </div>
                  </DropdownMenuCheckboxItem>
                  <DropdownMenuCheckboxItem
                    checked={showAlerts === 'faults' || showAlerts === 'both'}
                    onCheckedChange={handleFaultToggle}
                  >
                    <div className="flex items-center gap-2">
                      <div className="w-4 h-4 bg-fault rounded-sm flex items-center justify-center">
                        <AlertCircle className="w-2.5 h-2.5 text-white" />
                      </div>
                      <span>Faults {!filterContextLoading && `(${faultCount})`}</span>
                    </div>
                  </DropdownMenuCheckboxItem>
                </>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        {/* Mobile Center: All filters for map/dashboard */}
        <div className="flex items-center gap-1.5 overflow-visible">
          <IndustryFilter
            ref={industryButtonRef}
            units={units}
            highlight={showIndustryOnboarding}
          />
          <CompanyGroupFilters
            units={units}
            searchQuery={searchQuery}
            companyButtonRef={companyButtonRef}
            groupButtonRef={groupButtonRef}
            highlightCompany={showCompanyOnboarding}
            highlightGroup={showGroupOnboarding}
          />
          <UnitModelFilter
            ref={modelButtonRef}
            units={units}
            highlight={showModelOnboarding || selectedModels.length === 0}
          />
        </div>

        {/* Mobile Right: Refresh Button */}
        {/* <button
          onClick={handleRefresh}
          disabled={isRefreshing}
          className="flex items-center justify-center rounded hover:bg-accent transition-colors h-9 w-9 shrink-0"
        >
          <RefreshCw className={`h-4 w-4 ${isRefreshing || isSpinning ? 'animate-spin' : ''}`} />
        </button> */}
      </div>

      {/* Onboarding Tooltips - rendered via portal */}
      <OnboardingTooltip
        show={showIndustryOnboarding}
        message={industryMessage}
        targetRef={industryButtonRef}
        onDismiss={dismissIndustry}
      />
      <OnboardingTooltip
        show={showCompanyOnboarding}
        message={companyMessage}
        targetRef={companyButtonRef}
        onDismiss={dismissCompany}
      />
      <OnboardingTooltip
        show={showGroupOnboarding}
        message={groupMessage}
        targetRef={groupButtonRef}
        onDismiss={dismissGroup}
      />
      <OnboardingTooltip
        show={showModelOnboarding}
        message={modelMessage}
        targetRef={modelButtonRef}
        onDismiss={dismissModel}
      />
    </>
    );
  }

  // Desktop layout
  return (
    <>
    <div className="h-12 bg-card border-b border-border flex items-center pl-0 pr-6 gap-4 relative overflow-visible">
      {/* Left: Search Bar - Match sidebar width (360px) */}
      <div className="relative w-[240px] lg:w-[360px]">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground z-10" />
        <Input
          type="search"
          placeholder={isGroupAdmin ? "SEARCH LOCATION, UNIT, PRODUCTION, OR SITE" : "SEARCH LOCATION, UNIT, COMPANY, OR GROUP"}
          className="pl-9 h-9 text-xs bg-transparent border-0 rounded-none focus-visible:ring-0 focus-visible:ring-offset-0 focus-visible:outline-none focus:ring-0 focus:outline-none focus:border-0 active:border-0 w-full pr-8 [&::-webkit-search-cancel-button]:hidden [&::-webkit-search-decoration]:hidden"
          value={searchQuery}
          onChange={(e) => onSearchChange(e.target.value)}
        />
        {searchQuery && (
          <button
            onClick={() => onSearchChange('')}
            className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center justify-center hover:bg-accent rounded transition-colors h-6 w-6"
          >
            <X className="h-5 w-5 text-foreground" />
          </button>
        )}
      </div>

      {/* Center: Filter Group + Reset (absolutely centered) */}
      <div className="absolute left-1/2 -translate-x-1/2 flex items-center gap-2 overflow-visible">
        {/* Filter Group - All filters for map/dashboard */}
        <div className="flex items-center gap-2 py-1.5 overflow-visible">
          <IndustryFilter
            ref={industryButtonRef}
            units={units}
            highlight={showIndustryOnboarding}
          />
          <CompanyGroupFilters
            units={units}
            searchQuery={searchQuery}
            companyButtonRef={companyButtonRef}
            groupButtonRef={groupButtonRef}
            highlightCompany={showCompanyOnboarding}
            highlightGroup={showGroupOnboarding}
          />
          <UnitModelFilter
            ref={modelButtonRef}
            units={units}
            highlight={showModelOnboarding || selectedModels.length === 0}
          />
        </div>

        {/* Reset Button - right of filter group */}
        <button
          onClick={onClearFilters}
          disabled={isFiltersDefault}
          className="flex items-center text-muted-foreground hover:text-foreground transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          title="Reset filters"
        >
          <RotateCcw className="h-3.5 w-3.5" />
        </button>
      </div>

      {/* Right side content (pushed to far right) */}
      <div className="ml-auto flex items-center gap-4">

      {/* STATUS Dropdown - Visible on desktop < 2XL (compact) */}
      <div className="flex 2xl:hidden">
        <DropdownMenu open={open} onOpenChange={setOpen}>
          <DropdownMenuTrigger asChild>
            <button className="flex items-center gap-2 rounded hover:bg-accent transition-colors h-9 px-3">
              <Filter className="h-4 w-4" />
              <span className="hidden lg:inline text-xs font-medium tracking-wide">STATUS</span>
              <ChevronDown className="h-4 w-4" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56">
            <DropdownMenuLabel className="text-xs text-muted-foreground uppercase">Status</DropdownMenuLabel>
            <DropdownMenuCheckboxItem
              checked={statusFilters.has('online')}
              onCheckedChange={() => handleFilterToggle('online')}
            >
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 bg-success rounded-sm flex items-center justify-center">
                  <Power className="w-2.5 h-2.5 text-white" />
                </div>
                <span>Online {!filterContextLoading && `(${onlineCount})`}</span>
              </div>
            </DropdownMenuCheckboxItem>
            <DropdownMenuCheckboxItem
              checked={statusFilters.has('offline')}
              onCheckedChange={() => handleFilterToggle('offline')}
            >
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 bg-error rounded-sm flex items-center justify-center">
                  <Power className="w-2.5 h-2.5 text-white" />
                </div>
                <span>Offline {!filterContextLoading && `(${offlineCount})`}</span>
              </div>
            </DropdownMenuCheckboxItem>
            <DropdownMenuCheckboxItem
              checked={statusFilters.has('idle')}
              onCheckedChange={() => handleFilterToggle('idle')}
            >
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 bg-gray-600 rounded-sm flex items-center justify-center">
                  <Power className="w-2.5 h-2.5 text-white" />
                </div>
                <span>Idle {!filterContextLoading && `(${idleCount})`}</span>
              </div>
            </DropdownMenuCheckboxItem>

            {showErrorFilter && (
              <>
                <DropdownMenuSeparator />

                <DropdownMenuLabel className="text-xs text-muted-foreground uppercase">Error</DropdownMenuLabel>
                <DropdownMenuCheckboxItem
                  checked={showAlerts === 'warnings' || showAlerts === 'both'}
                  onCheckedChange={handleWarningToggle}
                >
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 bg-warning rounded-sm flex items-center justify-center">
                      <AlertTriangle className="w-2.5 h-2.5 text-white" />
                    </div>
                    <span>Warnings {!filterContextLoading && `(${warningCount})`}</span>
                  </div>
                </DropdownMenuCheckboxItem>
                <DropdownMenuCheckboxItem
                  checked={showAlerts === 'faults' || showAlerts === 'both'}
                  onCheckedChange={handleFaultToggle}
                >
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 bg-fault rounded-sm flex items-center justify-center">
                      <AlertCircle className="w-2.5 h-2.5 text-white" />
                    </div>
                    <span>Faults {!filterContextLoading && `(${faultCount})`}</span>
                  </div>
                </DropdownMenuCheckboxItem>
              </>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {/* Status Badges - Visible only on 2XL+ screens (full) */}
      <div className="hidden 2xl:flex items-center gap-3">
        <button
          onClick={() => handleFilterToggle('online')}
          className={`flex items-center gap-2 px-3 py-1.5 rounded transition-colors ${
            statusFilters.has('online') ? 'bg-success/20 border border-success/50' : 'bg-muted border border-border opacity-50'
          }`}
        >
          <div className="w-4 h-4 bg-success rounded-sm flex items-center justify-center">
            <Power className="w-2.5 h-2.5 text-white" />
          </div>
          <span className="text-xs font-medium">{!filterContextLoading ? onlineCount : '-'}</span>
        </button>
        <button
          onClick={() => handleFilterToggle('offline')}
          className={`flex items-center gap-2 px-3 py-1.5 rounded transition-colors ${
            statusFilters.has('offline') ? 'bg-error/20 border border-error/50' : 'bg-muted border border-border opacity-50'
          }`}
        >
          <div className="w-4 h-4 bg-error rounded-sm flex items-center justify-center">
            <Power className="w-2.5 h-2.5 text-white" />
          </div>
          <span className="text-xs font-medium">{!filterContextLoading ? offlineCount : '-'}</span>
        </button>
        <button
          onClick={() => handleFilterToggle('idle')}
          className={`flex items-center gap-2 px-3 py-1.5 rounded transition-colors ${
            statusFilters.has('idle') ? 'bg-gray-600/20 border border-gray-600/50' : 'bg-muted border border-border opacity-50'
          }`}
        >
          <div className="w-4 h-4 bg-gray-600 rounded-sm flex items-center justify-center">
            <Power className="w-2.5 h-2.5 text-white" />
          </div>
          <span className="text-xs font-medium">{!filterContextLoading ? idleCount : '-'}</span>
        </button>
        {showErrorFilter && (
          <>
            <button
              onClick={handleWarningToggle}
              aria-label={`Warnings ${!filterContextLoading ? warningCount : ''}`.trim()}
              className={`flex items-center gap-2 px-3 py-1.5 rounded transition-colors ${
                showAlerts === 'warnings' || showAlerts === 'both' ? 'bg-warning/20 border border-warning/50' : 'bg-muted border border-border opacity-50'
              }`}
            >
              <div className="w-4 h-4 bg-warning rounded-sm flex items-center justify-center">
                <AlertTriangle className="w-2.5 h-2.5 text-white" />
              </div>
              <span className="text-xs font-medium">{!filterContextLoading ? warningCount : '-'}</span>
            </button>
            <button
              onClick={handleFaultToggle}
              aria-label={`Faults ${!filterContextLoading ? faultCount : ''}`.trim()}
              className={`flex items-center gap-2 px-3 py-1.5 rounded transition-colors ${
                showAlerts === 'faults' || showAlerts === 'both' ? 'bg-fault/20 border border-fault/50' : 'bg-muted border border-border opacity-50'
              }`}
            >
              <div className="w-4 h-4 bg-fault rounded-sm flex items-center justify-center">
                <AlertCircle className="w-2.5 h-2.5 text-white" />
              </div>
              <span className="text-xs font-medium">{!filterContextLoading ? faultCount : '-'}</span>
            </button>
          </>
        )}
      </div>

        {/* Reload Button - Icon only */}
        <button
          onClick={handleRefresh}
          disabled={isRefreshing}
          className="text-muted-foreground hover:text-foreground transition-colors disabled:opacity-50"
          title={isRefreshing ? "Refreshing..." : "Reload"}
        >
          <RefreshCw className={`h-4 w-4 ${isRefreshing || isSpinning ? 'animate-spin' : ''}`} />
        </button>
      </div>
    </div>

    {/* Onboarding Tooltips - rendered via portal */}
    <OnboardingTooltip
      show={showIndustryOnboarding}
      message={industryMessage}
      targetRef={industryButtonRef}
      onDismiss={dismissIndustry}
    />
    <OnboardingTooltip
      show={showCompanyOnboarding}
      message={companyMessage}
      targetRef={companyButtonRef}
      onDismiss={dismissCompany}
    />
    <OnboardingTooltip
      show={showGroupOnboarding}
      message={groupMessage}
      targetRef={groupButtonRef}
      onDismiss={dismissGroup}
    />
    <OnboardingTooltip
      show={showModelOnboarding}
      message={modelMessage}
      targetRef={modelButtonRef}
      onDismiss={dismissModel}
    />
    </>
  );
}
